import { SavedFilter } from "../components/Shared/Filters/types";
import Customer from "./Customer";
import { NO_SUBSIDIARY } from "./Subsidiary";

/**
 * A structured, client-side filter over the in-memory customer list.
 *
 * Semantics: AND across groups, OR within a multi-select group. An omitted /
 * empty group is "no constraint". This is what the customer filter panel edits
 * and what a saved filter / org preset stores. The customer analogue of the
 * consultant `FilterDefinition` — simpler (no competence hierarchy).
 */
export interface CustomerFilterDefinition {
	/** undefined = both active and inactive */
	status?: "active" | "inactive";
	/** true = only customers originating from signup */
	isSignup?: boolean;
	pools?: string[];
	contractIds?: string[];
	/**
	 * subsidiary ids (OR within the group). The NO_SUBSIDIARY sentinel matches
	 * customers connected to the organisation directly (no subsidiary).
	 */
	subsidiaryIds?: string[];
	/** customer tag keys (org `customerTagTypes`) — matches customers carrying ANY selected key */
	tags?: string[];
}

/** A named, saved customer filter (org preset or personal). */
export type CustomerSavedFilter = SavedFilter<CustomerFilterDefinition>;

export const EMPTY_CUSTOMER_DEFINITION: CustomerFilterDefinition = {};

const FILTER_GROUPS: (keyof CustomerFilterDefinition)[] = [
	"status",
	"isSignup",
	"pools",
	"contractIds",
	"subsidiaryIds",
	"tags",
];

/** A definition with no active constraints (the "All" preset). */
export function isEmptyCustomerDefinition(def: CustomerFilterDefinition): boolean {
	return FILTER_GROUPS.every((key) => {
		const value = def[key];
		if (Array.isArray(value)) return value.length === 0;
		return value === undefined;
	});
}

/**
 * Canonical JSON for a definition so two definitions that differ only in array
 * ordering or empty/omitted groups compare equal. Used to tell which pill (if
 * any) the current filter corresponds to.
 */
function canonicalize(def: CustomerFilterDefinition): string {
	const normalized: Record<string, unknown> = {};

	FILTER_GROUPS.forEach((key) => {
		const value = def[key];
		if (Array.isArray(value)) {
			if (value.length) normalized[key] = [...value].sort();
		} else if (value !== undefined) {
			normalized[key] = value;
		}
	});

	return JSON.stringify(normalized);
}

export function customerDefinitionsEqual(
	a: CustomerFilterDefinition,
	b: CustomerFilterDefinition
): boolean {
	return canonicalize(a) === canonicalize(b);
}

/** Whether a single customer row satisfies the definition. */
export function matchesCustomer(c: Customer, def: CustomerFilterDefinition): boolean {
	if (def.status === "active" && !c.active) return false;
	if (def.status === "inactive" && c.active) return false;

	if (def.isSignup && !c.isSignup) return false;

	if (def.pools?.length) {
		const pool = c.pool;
		if (!pool || !def.pools.includes(pool)) return false;
	}

	if (def.contractIds?.length) {
		const contractId = c.contract?.id;
		if (!contractId || !def.contractIds.includes(contractId)) return false;
	}

	// OR within the group; NO_SUBSIDIARY matches customers without one.
	if (def.subsidiaryIds?.length) {
		const matched = c.subsidiaryId
			? def.subsidiaryIds.includes(c.subsidiaryId)
			: def.subsidiaryIds.includes(NO_SUBSIDIARY);
		if (!matched) return false;
	}

	// AND within the group: the customer must carry EVERY selected key
	// (mirrors the consultant filter's tag/certification semantics).
	if (def.tags?.length) {
		const carried = c.tags ?? [];
		if (!def.tags.every((key) => carried.some((tag) => tag.key === key))) return false;
	}

	return true;
}
